import { c as _c } from "react/compiler-runtime";
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useRef, useState } from "react";

// Most code copied from @react-google-maps/api

const defaultLoadScriptProps = {
  id: "script-loader",
  version: "weekly"
};
interface WindowWithGoogleMap extends Window {
  initMap?: () => void;
}
interface InjectScriptArg {
  url: string;
  id: string;
  nonce?: string;
}
export type Libraries = ("drawing" | "geometry" | "localContext" | "places" | "visualization")[];
interface LoadScriptUrlOptions {
  googleMapsApiKey: string | "";
  googleMapsClientId?: string;
  version?: string;
  language?: string;
  region?: string;
  libraries?: Libraries;
  channel?: string;
  mapIds?: string[];
}
function makeLoadScriptUrl({
  googleMapsApiKey,
  googleMapsClientId,
  version = "weekly",
  language,
  region,
  libraries,
  channel,
  mapIds
}: LoadScriptUrlOptions): string {
  const params = [];
  if (!(googleMapsApiKey && googleMapsClientId || !(googleMapsApiKey && googleMapsClientId))) {
    throw new Error("You need to specify either googleMapsApiKey or googleMapsClientId for @react-google-maps/api load script to work. You cannot use both at the same time.");
  }
  if (googleMapsApiKey) {
    params.push(`key=${googleMapsApiKey}`);
  } else if (googleMapsClientId) {
    params.push(`client=${googleMapsClientId}`);
  }
  if (version) {
    params.push(`v=${version}`);
  }
  if (language) {
    params.push(`language=${language}`);
  }
  if (region) {
    params.push(`region=${region}`);
  }
  if (libraries && libraries.length) {
    params.push(`libraries=${libraries.sort().join(",")}`);
  }
  if (channel) {
    params.push(`channel=${channel}`);
  }
  if (mapIds && mapIds.length) {
    params.push(`map_ids=${mapIds.join(",")}`);
  }
  params.push("callback=initMap");
  params.push("loading=async");
  return `https://maps.googleapis.com/maps/api/js?${params.join("&")}`;
}
const isRobotoStyle = (element: HTMLElement): boolean => {
  // roboto font download
  if ((element as HTMLLinkElement).href && ((element as HTMLLinkElement).href.indexOf("https://fonts.googleapis.com/css?family=Roboto") === 0 || (element as HTMLLinkElement).href.indexOf("https://fonts.googleapis.com/css?family=Google+Sans+Text") === 0)) {
    return true;
  }
  // roboto style elements
  if (element.tagName.toLowerCase() === "style" &&
  // @ts-ignore
  element.styleSheet &&
  // @ts-ignore
  element.styleSheet.cssText &&
  // @ts-ignore
  element.styleSheet.cssText.replace("\r\n", "").indexOf(".gm-style") === 0) {
    // @ts-ignore
    element.styleSheet.cssText = "";
    return true;
  }
  // roboto style elements for other browsers
  if (element.tagName.toLowerCase() === "style" && element.innerHTML && element.innerHTML.replace("\r\n", "").indexOf(".gm-style") === 0) {
    element.innerHTML = "";
    return true;
  }
  // when google tries to add empty style
  if (element.tagName.toLowerCase() === "style" &&
  // @ts-ignore
  !element.styleSheet && !element.innerHTML) {
    return true;
  }
  return false;
};

// Preventing the Google Maps library from downloading an extra font
const preventGoogleFonts = (): void => {
  // we override these methods only for one particular head element
  // default methods for other elements are not affected
  const head = document.getElementsByTagName("head")[0];
  const trueInsertBefore = head.insertBefore.bind(head);

  // @ts-ignore
  head.insertBefore = function insertBefore(newElement: HTMLElement, referenceElement: HTMLElement): void {
    if (!isRobotoStyle(newElement)) {
      Reflect.apply(trueInsertBefore, head, [newElement, referenceElement]);
    }
  };
  const trueAppend = head.appendChild.bind(head);

  // @ts-ignore
  head.appendChild = function appendChild(textNode: HTMLElement): void {
    if (!isRobotoStyle(textNode)) {
      Reflect.apply(trueAppend, head, [textNode]);
    }
  };
};
const isBrowser: boolean = typeof document !== "undefined";
const injectScript = ({
  url,
  id,
  nonce
}: InjectScriptArg): Promise<unknown> => {
  if (!isBrowser) {
    return Promise.reject(new Error("document is undefined"));
  }
  return new Promise(function injectScriptCallback(resolve, reject) {
    const existingScript = document.getElementById(id) as HTMLScriptElement | undefined;
    const windowWithGoogleMap: WindowWithGoogleMap = window;
    if (existingScript) {
      // Same script id/url: keep same script
      const dataStateAttribute = existingScript.getAttribute("data-state");
      if (existingScript.src === url && dataStateAttribute !== "error") {
        if (dataStateAttribute === "ready") {
          return resolve(id);
        } else {
          const originalInitMap = windowWithGoogleMap.initMap;
          const originalErrorCallback = existingScript.onerror;
          windowWithGoogleMap.initMap = function initMap(): void {
            if (originalInitMap) {
              originalInitMap();
            }
            resolve(id);
          };
          existingScript.onerror = function (err): void {
            if (originalErrorCallback) {
              originalErrorCallback(err);
            }
            reject(err);
          };
          return;
        }
      }
      // Same script id, but either
      // 1. requested URL is different
      // 2. script failed to load
      else {
        existingScript.remove();
      }
    }
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = url;
    script.id = id;
    script.async = true;
    script.nonce = nonce;
    script.onerror = function onerror(err): void {
      script.setAttribute("data-state", "error");
      reject(err);
    };
    windowWithGoogleMap.initMap = function onload(): void {
      script.setAttribute("data-state", "ready");
      resolve(id);
    };
    document.head.appendChild(script);
  }).catch(err => {
    console.error("injectScript error: ", err);
    throw err;
  });
};

// eslint-disable-next-line import/no-unused-modules
export interface UseLoadScriptOptions extends LoadScriptUrlOptions {
  id?: string;
  nonce?: string;
  preventGoogleFontsLoading?: boolean;
  loadNow: boolean;
}
let previouslyLoadedUrl: string;
export function useLoadScriptLazy(t0) {
  const $ = _c(32);
  const {
    id: t1,
    version: t2,
    nonce,
    googleMapsApiKey,
    googleMapsClientId,
    language,
    region,
    libraries,
    preventGoogleFontsLoading,
    channel,
    mapIds,
    loadNow
  } = t0;
  const id = t1 === undefined ? defaultLoadScriptProps.id : t1;
  const version = t2 === undefined ? defaultLoadScriptProps.version : t2;
  const isMounted = useRef(false);
  const [isLoaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(undefined);
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = function trackMountedState() {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
      };
    };
    t4 = [];
    $[0] = t3;
    $[1] = t4;
  } else {
    t3 = $[0];
    t4 = $[1];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[2] !== preventGoogleFontsLoading) {
    t5 = function applyPreventGoogleFonts() {
      if (isBrowser && preventGoogleFontsLoading) {
        preventGoogleFonts();
      }
    };
    t6 = [preventGoogleFontsLoading];
    $[2] = preventGoogleFontsLoading;
    $[3] = t5;
    $[4] = t6;
  } else {
    t5 = $[3];
    t6 = $[4];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[5] !== isLoaded) {
    t7 = function validateLoadedState() {
      if (isLoaded) {
        if (!!!window.google) {
          throw new Error("useLoadScript was marked as loaded, but window.google is not present. Something went wrong.");
        }
      }
    };
    t8 = [isLoaded];
    $[5] = isLoaded;
    $[6] = t7;
    $[7] = t8;
  } else {
    t7 = $[6];
    t8 = $[7];
  }
  useEffect(t7, t8);
  let t9;
  if ($[8] !== channel || $[9] !== googleMapsApiKey || $[10] !== googleMapsClientId || $[11] !== language || $[12] !== libraries || $[13] !== mapIds || $[14] !== region || $[15] !== version) {
    t9 = makeLoadScriptUrl({
      version,
      googleMapsApiKey,
      googleMapsClientId,
      language,
      region,
      libraries,
      channel,
      mapIds
    });
    $[8] = channel;
    $[9] = googleMapsApiKey;
    $[10] = googleMapsClientId;
    $[11] = language;
    $[12] = libraries;
    $[13] = mapIds;
    $[14] = region;
    $[15] = version;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const url = t9;
  let t10;
  let t11;
  if ($[17] !== googleMapsApiKey || $[18] !== googleMapsClientId || $[19] !== id || $[20] !== loadNow || $[21] !== nonce || $[22] !== url) {
    t10 = () => {
      const loadScriptAndModifyLoadedState = async function loadScriptAndModifyLoadedState() {
        if (!isBrowser || !loadNow) {
          return;
        }
        const setLoadedIfMounted = function setLoadedIfMounted() {
          if (isMounted.current) {
            setLoaded(true);
            previouslyLoadedUrl = url;
          }
        };
        if (window.google && window.google.maps && previouslyLoadedUrl === url) {
          setLoadedIfMounted();
          return;
        }
        ;
        try {
          await injectScript({
            id,
            url,
            nonce
          });
          setLoadedIfMounted();
        } catch (t12) {
          const err = t12;
          if (isMounted.current) {
            setLoadError(err as Error);
          }
          console.warn(`
            There has been an Error with loading Google Maps API script, please check that you provided correct google API key (${googleMapsApiKey || "-"}) or Client ID (${googleMapsClientId || "-"})
            Otherwise it is a Network issue.
          `);
          console.error(err);
        }
      };
      loadScriptAndModifyLoadedState();
    };
    t11 = [id, url, nonce, loadNow, googleMapsApiKey, googleMapsClientId];
    $[17] = googleMapsApiKey;
    $[18] = googleMapsClientId;
    $[19] = id;
    $[20] = loadNow;
    $[21] = nonce;
    $[22] = url;
    $[23] = t10;
    $[24] = t11;
  } else {
    t10 = $[23];
    t11 = $[24];
  }
  useEffect(t10, t11);
  const prevLibraries = useRef(undefined);
  let t12;
  let t13;
  if ($[25] !== libraries) {
    t12 = function checkPerformance() {
      if (prevLibraries.current && libraries !== prevLibraries.current) {
        console.warn("Performance warning! LoadScript has been reloaded unintentionally! You should not pass `libraries` prop as new array. Please keep an array of libraries as static class property for Components and PureComponents, or just a const variable outside of component, or somewhere in config files or ENV variables");
      }
      prevLibraries.current = libraries;
    };
    t13 = [libraries];
    $[25] = libraries;
    $[26] = t12;
    $[27] = t13;
  } else {
    t12 = $[26];
    t13 = $[27];
  }
  useEffect(t12, t13);
  let t14;
  if ($[28] !== isLoaded || $[29] !== loadError || $[30] !== url) {
    t14 = {
      isLoaded,
      loadError,
      url
    };
    $[28] = isLoaded;
    $[29] = loadError;
    $[30] = url;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
}